/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Table, 
  Button, 
  Form, 
  Badge, 
  Alert,
  Modal,
  Spinner,
  Pagination,
  InputGroup
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { formatErrorForReact } from '../../../helpers/utils';

interface Report {
  id: string;
  title: string;
  reportType: string;
  status: string;
  createdAt: string;
  propertyData: {
    address: string;
    sqft: number;
    bedrooms: number;
    bathrooms: number;
  };
  predictionData: {
    value: number;
    confidence_score: number;
  };
  lead?: {
    id: string;
    lead_first_name: string;
    lead_last_name: string;
    lead_email: string;
  };
}

interface ReportsResponse {
  reports: Report[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const SmartCMAReports: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [reportTypeFilter, setReportTypeFilter] = useState('');
  
  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<Report | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch reports
  const fetchReports = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...(searchTerm && { searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(reportTypeFilter && { reportType: reportTypeFilter })
      });

      const response = await fetch(`/api/smart-cma?${params}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch reports');
      }

      const data: { success: boolean; data: ReportsResponse } = await response.json();
      
      if (data.success) {
        setReports(data.data.reports);
        setPagination(data.data.pagination);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  // Delete report
  const deleteReport = async () => {
    if (!reportToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`/api/smart-cma/${reportToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchReports(); // Refresh the list
        setShowDeleteModal(false);
        setReportToDelete(null);
      } else {
        throw new Error('Failed to delete report');
      }
    } catch (err) {
      alert('Error deleting report: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle search and filters
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchReports();
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  // Format functions
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      'COMPLETED': 'success',
      'DRAFT': 'warning',
      'GENERATING': 'info',
      'ERROR': 'danger',
      'PUBLISHED': 'primary',
      'ARCHIVED': 'secondary'
    };
    
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  // Effects
  useEffect(() => {
    fetchReports();
  }, [pagination.page]);

  // Delete confirmation modal
  const DeleteConfirmModal = () => (
    <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Delete Report</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Are you sure you want to delete this report?</p>
        <p><strong>{reportToDelete?.title}</strong></p>
        <p className="text-muted">This action cannot be undone.</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
          Cancel
        </Button>
        <Button 
          variant="danger" 
          onClick={deleteReport}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Deleting...
            </>
          ) : (
            'Delete Report'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className="container-fluid">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">Smart CMA Reports</h4>
                <small className="text-muted">Manage your saved property analysis reports</small>
              </div>
              <Link to="/price-prediction-model">
                <Button variant="primary">
                  New Smart CMA
                </Button>
              </Link>
            </Card.Header>
            
            <Card.Body>
              {/* Filters */}
              <Row className="mb-4">
                <Col md={4}>
                  <InputGroup>
                    <Form.Control
                      placeholder="Search reports..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button variant="outline-secondary" onClick={handleSearch}>
                      Search
                    </Button>
                  </InputGroup>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="DRAFT">Draft</option>
                    <option value="GENERATING">Generating</option>
                    <option value="PUBLISHED">Published</option>
                    <option value="ARCHIVED">Archived</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={reportTypeFilter}
                    onChange={(e) => setReportTypeFilter(e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="SMART_CMA">Smart CMA</option>
                    <option value="MARKET_ANALYSIS">Market Analysis</option>
                    <option value="PROPERTY_VALUATION">Property Valuation</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Button variant="outline-secondary" onClick={fetchReports} className="w-100">
                    Refresh
                  </Button>
                </Col>
              </Row>

              {/* Error Message */}
              {error && (
                <Alert variant="danger" className="mb-4">
                  {formatErrorForReact(error)}
                </Alert>
              )}

              {/* Loading State */}
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                  <p className="mt-2 text-muted">Loading reports...</p>
                </div>
              ) : (
                <>
                  {/* Reports Table */}
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Property</th>
                        <th>Predicted Value</th>
                        <th>Status</th>
                        <th>Lead</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-4 text-muted">
                            No reports found. <Link to="/price-prediction-model">Create your first Smart CMA report</Link>
                          </td>
                        </tr>
                      ) : (
                        reports.map((report) => (
                          <tr key={report.id}>
                            <td>
                              <div>
                                <strong>{report.propertyData.address}</strong>
                                <br />
                                <small className="text-muted">
                                  {report.propertyData.bedrooms} bed, {report.propertyData.bathrooms} bath, {report.propertyData.sqft.toLocaleString()} sqft
                                </small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>{formatCurrency(report.predictionData.value)}</strong>
                                <br />
                                <small className="text-muted">
                                  {(report.predictionData.confidence_score * 100).toFixed(1)}% confidence
                                </small>
                              </div>
                            </td>
                            <td>{getStatusBadge(report.status)}</td>
                            <td>
                              {report.lead ? (
                                <div>
                                  <strong>{report.lead.lead_first_name} {report.lead.lead_last_name}</strong>
                                  <br />
                                  <small className="text-muted">{report.lead.lead_email}</small>
                                </div>
                              ) : (
                                <span className="text-muted">Not assigned</span>
                              )}
                            </td>
                            <td>
                              <small>{formatDate(report.createdAt)}</small>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  as={Link}
                                  to={`/smart-cma-report/${report.id}`}
                                >
                                  View
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => {
                                    setReportToDelete(report);
                                    setShowDeleteModal(true);
                                  }}
                                >
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div className="text-muted">
                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} reports
                      </div>
                      <Pagination>
                        <Pagination.Prev 
                          disabled={pagination.page === 1}
                          onClick={() => handlePageChange(pagination.page - 1)}
                        />
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          const pageNum = Math.max(1, pagination.page - 2) + i;
                          if (pageNum <= pagination.totalPages) {
                            return (
                              <Pagination.Item
                                key={pageNum}
                                active={pageNum === pagination.page}
                                onClick={() => handlePageChange(pageNum)}
                              >
                                {pageNum}
                              </Pagination.Item>
                            );
                          }
                          return null;
                        })}
                        <Pagination.Next 
                          disabled={pagination.page === pagination.totalPages}
                          onClick={() => handlePageChange(pagination.page + 1)}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <DeleteConfirmModal />
    </div>
  );
};

export default SmartCMAReports; 